import { BaseService } from "./base.service";
import { IItem, IItemTransferMode } from "models/ItemModel";
import { GLAccountModel } from "models/Requisition/GLAccountModel";
import {
	GLBudgetModel,
	IContractGLBudgetModel,
} from "models/Requisition/GLBudgetModel";
import { TableFilterModel } from "models/TableFilterModel";
import ItemDetailModel, {
	CopyItemDetail,
	ItemDetailOnPoLine,
} from "models/Item/ItemDetailModel";
import { IItemUOM } from "models/Item/itemUOMModel";
import {
	IItemByPurchaseCategory,
	IItemNameModel,
} from "../models/Item/ItemNameModel";
import { IItemGridDetail } from "models/Item/ItemGridDetailModel";
import { TransferAvailableQuantityDetail } from "models/Requisition/TransferAvailableQuantityDetail";
import { IItemAvailability } from "models/Item/itemAvailabilityModel";
import { KeyFilterModel } from "models/SystemScheduler/SystemSchedulerFilterModel";
import { IErpDbCompany } from "models/ErpDbCompanyModel";
import { IDepartment } from "models/DepartmentModel";
import { ISupportItem } from "models/Item/supportItemModel";
import { IItemStepHistory } from "models/Item/itemStepHistory";

class ItemServiceInternal extends BaseService {
	getItemList = async (
		erpDbCompanyId: number,
		purchaseCategoryId: number,
		reasonCodeId: number,
		buyingDepartmentId: number,
		selectedItemIds: string,
		transactionTypeId: number | null
	): Promise<IItem[] | IItemTransferMode[]> => {
		let urlParams = {} as any;
		if (transactionTypeId && transactionTypeId > 0)
			urlParams["transactionTypeId"] = transactionTypeId.toString();
		if (selectedItemIds)
			urlParams["selectedItemIds"] = selectedItemIds.toString();

		return this.callApi<IItem[] | IItemTransferMode[]>(
			`Item/GetItemList/${erpDbCompanyId}/${purchaseCategoryId}/${reasonCodeId}/${buyingDepartmentId}`,
			"GET",
			Object.keys(urlParams).length ? new URLSearchParams(urlParams) : undefined
		);
	};

	getGlAccountDetails = async (
		itemId: number,
		buyingDepartmentId: number,
		programmeId: number,
		regionId: number,
		divisionId: number
	): Promise<GLAccountModel | null> => {
		let urlParams = {} as any;
		if (regionId && regionId > 0) urlParams["regionId"] = regionId.toString();
		if (divisionId && divisionId > 0)
			urlParams["divisionId"] = divisionId.toString();

		return this.callApi<GLAccountModel | null>(
			`GlAccount/GetGlAccountDetails/${itemId}/${buyingDepartmentId}/${programmeId}`,
			"GET",
			Object.keys(urlParams).length ? new URLSearchParams(urlParams) : undefined
		);
	};

	getContractItemList = async (
		contractId: number,
		selectedItemIds: string
	): Promise<IItem[]> =>
		this.callApi<IItem[]>(
			`Item/GetContractItemList/${contractId}`,
			"GET",
			selectedItemIds
				? new URLSearchParams({
						selectedItemIds: selectedItemIds,
				  })
				: undefined
		);

	getItemUOMList = async (): Promise<IItemUOM[]> =>
		this.callApi<IItemUOM[]>(`ItemUom/GetItemUomList`, "GET");

	getAllItemList = async (): Promise<IItemNameModel[]> =>
		this.callApi<IItemNameModel[]>(`Item/GetAllItemList`, "GET");

	getItemGridList = async (
		model: TableFilterModel
	): Promise<IItemGridDetail[]> =>
		this.callApi<IItemGridDetail[]>(
			`Item/GetItemGridList`,
			"POST",
			undefined,
			model
		);

	getItemDetail = async (
		itemId: number,
		limitedItemStepId: number
	): Promise<ItemDetailModel> =>
		this.callApi<ItemDetailModel>(
			`Item/GetItem/${itemId}`,
			"GET",
			limitedItemStepId > 0
				? new URLSearchParams({
						limitedItemStepId: limitedItemStepId.toString(),
				  })
				: undefined
		);

	updateItemDetail = async (model: ItemDetailModel): Promise<undefined> =>
		this.callApi<undefined>(
			`Item/Update/${model.itemId}`,
			"PUT",
			undefined,
			model
		);

	getContractBudgetDetail = async (
		model: IContractGLBudgetModel
	): Promise<GLBudgetModel[]> => {
		return this.callApi<GLBudgetModel[]>(
			`ContractBudget/CheckAvailableContractBudget`,
			"POST",
			undefined,
			model
		);
	};

	checkAvailableQuantity = async (
		itemId: number,
		quantityRequired: number
	): Promise<TransferAvailableQuantityDetail> =>
		this.callApi<TransferAvailableQuantityDetail>(
			`InventoryMaintenance/CheckAvailableQuantity/${itemId}/${quantityRequired}`,
			"GET"
		);

	getItemAvailabilityList = async (): Promise<IItemAvailability[]> =>
		this.callApi<IItemAvailability[]>(`ItemAvailability/GetAll`, "GET");

	sendGlAccountCreationRequestEmail = async (
		itemId: number,
		buyingDepartmentId: number,
		programmeId: number,
		divisionId: number,
		regionId: number,
		requestedBy: number
	): Promise<boolean> => {
		let urlParams = {} as any;
		if (divisionId > 0) urlParams["divisionId"] = divisionId.toString();
		if (regionId > 0) urlParams["regionId"] = regionId.toString();

		return this.callApi<boolean>(
			`GlAccount/SendGlAccountCreationRequestEmail/${itemId}/${buyingDepartmentId}/${programmeId}/${requestedBy}`,
			"POST",
			Object.keys(urlParams).length ? new URLSearchParams(urlParams) : undefined
		);
	};

	getItemListByPurchaseCategory = async (
		purchaseCategoryId: number
	): Promise<IItemByPurchaseCategory[]> =>
		this.callApi<IItemByPurchaseCategory[]>(
			`Item/GetItemByPurchaseCategoryId/${purchaseCategoryId}`,
			"GET"
		);

	saveItemDetail = async (model: ItemDetailModel): Promise<undefined> =>
		this.callApi<undefined>(`Item/Save`, "POST", undefined, model);

	getItemNameList = async (model: KeyFilterModel): Promise<IItemNameModel[]> =>
		this.callApi<IItemNameModel[]>(
			`Item/GetItemNameList`,
			"POST",
			undefined,
			model
		);

	checkIsItemAlreadyUsed = async (
		itemId: number,
		isForStatus: boolean
	): Promise<boolean> =>
		this.callApi<boolean>(
			`Item/CheckIsItemAlreadyUsed/${itemId}`,
			"GET",
			isForStatus
				? new URLSearchParams({
						isForStatus: isForStatus.toString(),
				  })
				: undefined
		);

	deleteItem = async (itemId: number): Promise<undefined> =>
		this.callApi<undefined>(`Item/Delete/${itemId}`, "DELETE");

	getItemTypeList = async (): Promise<{ itemTypeId: number; name: string }[]> =>
		this.callApi<{ itemTypeId: number; name: string }[]>(
			`ItemType/GetAll`,
			"GET"
		);

	getErpDbCompanyListByItemId = async (
		itemId: number
	): Promise<IErpDbCompany[]> =>
		this.callApi<IErpDbCompany[]>(
			`Item/GetErpDbCompanyListByItemId/${itemId}`,
			"GET"
		);

	copyItemDetail = async (model: CopyItemDetail): Promise<undefined> =>
		this.callApi<undefined>(
			`Item/Copy/${model.itemId}/${model.erpDbCompanyId}/${model.itemCode}`,
			"GET"
		);

	getCentralizedDepartmentsByItemId = async (
		itemId: number
	): Promise<IDepartment[]> =>
		this.callApi<IDepartment[]>(
			`Item/GetCentralizedDepartmentsByItemId/${itemId}`,
			"GET"
		);

	getItemDetailOnPoLine = async (
		contractId: number,
		programmeId: number,
		buyingDepartmentId: number,
		itemId: number,
		csdCommodityId: number,
		regionId: number,
		divisionId: number
	): Promise<ItemDetailOnPoLine[]> => {
		let urlParams = {} as any;
		if (regionId && regionId > 0) urlParams["regionId"] = regionId.toString();
		if (divisionId && divisionId > 0)
			urlParams["divisionId"] = divisionId.toString();

		return this.callApi<ItemDetailOnPoLine[]>(
			`ContractLine/GetItemDetailOnPoLine/${contractId}/${programmeId}/${buyingDepartmentId}/${itemId}/${csdCommodityId}`,
			"GET",
			Object.keys(urlParams).length ? new URLSearchParams(urlParams) : undefined
		);
	};

	validateItemSegment = async (
		erpDbCompanyId: number,
		purchaseCategoryId: number,
		itemCode: string
	): Promise<string> =>
		this.callApi<string>(
			`Item/ValidateItemSegment/${erpDbCompanyId}/${purchaseCategoryId}/${itemCode}`,
			"GET"
		);

	getAllSupportItemList = async (): Promise<ISupportItem[]> =>
		this.callApi<ISupportItem[]>(`Item/GetAllSupportItemList`, "GET");

	getItemStepHistoryList = async (
		itemId: number
	): Promise<IItemStepHistory[]> =>
		this.callApi<IItemStepHistory[]>(
			`LimitedItemStep/GetLimitedItemStepList/${itemId}`,
			"GET"
		);

	getAllTravelItems = async (): Promise<
		{ travelItemId: number; name: string }[]
	> =>
		this.callApi<{ travelItemId: number; name: string }[]>(
			"TravelItem/GetAll",
			"GET"
		);

	checkTravelItemExists = async (
		erpDbCompanyId: number,
		name: string
	): Promise<number> =>
		this.callApi<number>(
			`Item/CheckTravelItemExists/${erpDbCompanyId}/${encodeURIComponent(
				name
			)}`,
			"GET"
		);
}

export const ItemService = new ItemServiceInternal();
